/**
 * 只挪镜头的口子（UI-WORLD-LIVING-117-2b，I 10-16 07:20 裁定）：就近面板放不下生活照时，地图把被点的那一位轻轻往上挪，给下面腾出地方。
 * - panBy(dx, dy, instant)：画面内容往右挪 dx、往下挪 dy（px；负数往左、往上）。instant 为 true 时直接跳到位（减少动态），否则约 PAN_MS 挪过去。
 * - reach()：被点的那一位最多还能往上挪多少（px），放置只按这个量排档。示意图、星球总览 2D 不限（不给这个方法）；高德读好了不限、没读好是 0（不挪）；
 *   3D 星球按球的几何和镜头纬度上限算（评审 R2 阻断：原来按「能挪到顶栏」排档，球转不到那么多，卡就整张盖在那一位上）。
 *   挪完地图那边总会再放一次（真挪了多少按那时量到的算），所以 panBy 不用回挪了多少。
 * 由各底图自己实现：shared/map 的示意图（SchematicMapSurface）、星球总览 2D（GlobeFallback）、城市层高德（RegionAmap）、3D 星球（world/globeCamera）。
 */
export type MapCamera = {
  panBy: (dx: number, dy: number, instant: boolean) => void;
  reach?: () => number;
};

/** 轻移地图用多久（ms） */
export const PAN_MS = 240;

export function prefersReducedMotion(): boolean {
  return typeof window !== "undefined" && typeof window.matchMedia === "function" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

/**
 * 示意图那两种（视图是 tx、ty）用：按帧把位移分给 step，每次给这一帧该挪的增量（缓出）。
 * instant 为 true、或没有 requestAnimationFrame 时，一次给完。返回取消函数：取消后剩下的不再挪。
 */
export function tweenPan(step: (dx: number, dy: number) => void, dx: number, dy: number, instant: boolean): () => void {
  if (instant || typeof requestAnimationFrame !== "function") {
    step(dx, dy);
    return () => {};
  }
  let start: number | null = null;
  let doneX = 0;
  let doneY = 0;
  let frame = 0;
  const tick = (time: number) => {
    if (start === null) start = time;
    const k = Math.min(1, (time - start) / PAN_MS);
    const eased = 1 - (1 - k) ** 3;
    const x = dx * eased;
    const y = dy * eased;
    step(x - doneX, y - doneY);
    doneX = x;
    doneY = y;
    if (k < 1) frame = requestAnimationFrame(tick);
  };
  frame = requestAnimationFrame(tick);
  return () => cancelAnimationFrame(frame);
}
